import AddIcon from "@mui/icons-material/Add";
import { MenuItem, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import { getServicesWoLoader } from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { addIcon } from "../../../util/CommonIconStyle";
import "./ReminderTemplate.css";

const expireUnits = [
  { name: "days", value: "days" },
  { name: "weeks", value: "weeks" },
  { name: "months", value: "months" },
];

const getAnchorStep = () => ({
  id: `step-${Date.now()}`,
  name: "",
  isAnchor: true,
  dueAfterDays: "21",
  remindBeforeDays: "7",
  remindEveryDays: "7",
  expireAfter: "30",
  expireUnit: "days",
});

const getFollowUpStep = () => ({
  id: `step-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: "",
  isAnchor: false,
  dueAfterDays: "21",
  remindBeforeDays: "7",
  remindEveryDays: "7",
  expireAfter: "30",
  expireUnit: "days",
});

const getInitialSteps = () => [getAnchorStep()];

const initValues = {
  templateName: "",
  linkedServices: [],
  occurrenceLabel: "Dose",
  steps: getInitialSteps(),
};

const ReminderTemplateModal = ({
  open,
  onClose,
  selectedTemp,
  onSave,
  isSaving = false,
}) => {
  const dispatch = useDispatch();
  const clinicServices = useSelector((state) => state?.clinic?.services);
  const [values, setValues] = useState(initValues);
  const [errs, setErrs] = useState({});
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const mandateServices = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return Array.isArray(reqData?.services) ? reqData.services : [];
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  const serviceList = useMemo(() => {
    const source =
      clinicServices?.data?.length > 0
        ? clinicServices.data
        : Array.isArray(clinicServices) && clinicServices.length > 0
          ? clinicServices
          : mandateServices;
    return (source ?? [])
      ?.filter((ser) => ser?.active !== "N")
      ?.map((ser) => ({
        name: ser?.serviceName,
        serviceName: ser?.serviceName,
        value: ser?.serviceId ?? ser?.serviceName,
        serviceId: ser?.serviceId,
        category: ser?.category ?? "",
        subCategory: ser?.subCategory ?? "",
      }));
  }, [clinicServices, mandateServices]);

  const mapLinkedServices = (linked = []) =>
    (linked ?? [])
      ?.map((ser) => {
        const id =
          typeof ser === "object" && ser !== null
            ? (ser?.serviceId ?? ser?.value)
            : ser;
        if (id == null || id === "") return null;
        const match = serviceList?.find(
          (s) => String(s?.serviceId) === String(id),
        );
        if (match) return match;
        return {
          name: String(id),
          value: id,
          serviceId: id,
        };
      })
      ?.filter(Boolean);

  useEffect(() => {
    if (!open) return;
    dispatch(getServicesWoLoader("?serviceData=true"));
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    if (selectedTemp) {
      const linked = selectedTemp?.linkedServices ?? [];
      const steps = selectedTemp?.steps ?? [];
      setValues({
        templateName: selectedTemp?.templateName ?? "",
        linkedServices: mapLinkedServices(linked),
        occurrenceLabel: selectedTemp?.occurrenceLabel || "Dose",
        steps:
          steps?.length > 0
            ? steps.map((st, index) => ({
                id: st?.id ?? `step-${st?.stepOrder ?? index + 1}`,
                stepId: st?.stepId ?? null,
                name: st?.name ?? st?.stepName ?? "",
                isAnchor: Boolean(st?.isAnchor) || index === 0,
                dueAfterDays: st?.dueAfterDays ?? "21",
                remindBeforeDays: st?.remindBeforeDays ?? "7",
                remindEveryDays: st?.remindEveryDays ?? "7",
                expireAfter: st?.expireAfter ?? "30",
                expireUnit: st?.expireUnit ?? "days",
              }))
            : getInitialSteps(),
      });
    } else {
      setValues({ ...initValues, steps: getInitialSteps() });
    }
    setErrs({});
  }, [open, selectedTemp]);

  useEffect(() => {
    if (!open || !serviceList?.length) return;
    setValues((prev) => ({
      ...prev,
      linkedServices: mapLinkedServices(prev?.linkedServices ?? []),
    }));
  }, [open, serviceList]);

  const occurrence = values?.occurrenceLabel?.trim() || "Dose";

  const onFieldChange = (name, value) => {
    setValues({ ...values, [name]: value });
    if (name === "templateName" && value?.length > 0) {
      setErrs({ ...errs, templateName: false });
    }
  };

  const onStepChange = (id, name, value) => {
    setValues({
      ...values,
      steps: values?.steps?.map((st) =>
        st?.id === id ? { ...st, [name]: value } : st,
      ),
    });
  };

  const onAddStep = () => {
    if ((values?.steps?.length ?? 0) >= 2) return;
    setValues({
      ...values,
      steps: [...(values?.steps ?? []), getFollowUpStep()],
    });
  };

  const onRemoveStep = (id) => {
    setValues({
      ...values,
      steps: values?.steps?.filter((st) => st?.id !== id),
    });
  };

  const getApiPayload = () => {
    const occurrenceLabel = values?.occurrenceLabel?.trim() || "Dose";
    const data = {
      templateName: values?.templateName?.trim(),
      occurrenceLabel,
      linkedServices: (values?.linkedServices ?? [])
        ?.filter((ser) => ser?.serviceId || ser?.value)
        ?.map((ser) => ser?.serviceId ?? ser?.value),
      steps: (values?.steps ?? [])?.map((st, index) => {
        const stepName = st?.name?.trim() || `${occurrenceLabel} ${index + 1}`;
        const step = {
          stepId: st?.stepId ?? null,
          stepOrder: index + 1,
          stepName,
        };
        if (Boolean(st?.isAnchor) || index === 0) {
          step.dueAfterDays = 0;
          step.remindBeforeDays = 0;
          step.remindEveryDays = 0;
          step.expireAfter = 0;
          step.expireUnit = "days";
        } else {
          step.dueAfterDays = Number(st?.dueAfterDays) || 0;
          step.remindBeforeDays = Number(st?.remindBeforeDays) || 0;
          step.remindEveryDays = Number(st?.remindEveryDays) || 0;
          step.expireAfter = Number(st?.expireAfter) || 0;
          step.expireUnit = st?.expireUnit || "days";
        }
        return step;
      }),
    };
    return data;
  };

  const handleSave = () => {
    if (isSaving) return;
    if (!values?.templateName?.trim()) {
      setErrs({ templateName: true });
      return;
    }
    onSave?.(getApiPayload());
  };

  const renderCompactInput = (value, onChange) => (
    <div className="rem-temp-num">
      <CustomTextField
        fullWidth
        type="number"
        value={value}
        handleChange={(e) => onChange(e?.target?.value)}
      />
    </div>
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Reminder Template"
      modal
      modalWidth={52}
    >
      <div className="rem-temp-modal">
        <div className="mb10">
          <div className="rem-temp-label">Template name</div>
          <CustomTextField
            fullWidth
            placeholder="Puppy Core Vaccination Series"
            value={values?.templateName}
            handleChange={(e) =>
              onFieldChange("templateName", e?.target?.value)
            }
            error={errs?.templateName}
            helperText={errs?.templateName ? "This field is required" : ""}
          />
        </div>

        <div className="mb10">
          <div className="rem-temp-label">Linked service</div>
          <SearchableSelect
            list={serviceList}
            value={values?.linkedServices ?? []}
            handleChange={(e) =>
              onFieldChange("linkedServices", e?.target?.value ?? [])
            }
            multiSelectTagCheck
            maxFilteredOptions={100}
            searchFields={["name", "serviceName", "category", "subCategory"]}
            backgroundColor={AppColors.white}
          />
        </div>

        <div className="mb20">
          <div className="rem-temp-label">What do you call one occurrence?</div>
          <CustomTextField
            fullWidth
            placeholder="Dose"
            value={values?.occurrenceLabel}
            handleChange={(e) =>
              onFieldChange("occurrenceLabel", e?.target?.value)
            }
          />
        </div>

        <div className="rem-temp-steps-head">
          Steps — chained, each due date calculated once the previous step is
          completed
        </div>

        <div className="rem-temp-steps">
          {values?.steps?.map((step, index) => (
            <div className="rem-temp-step" key={step?.id}>
              <div className="rem-temp-step-num">{index + 1}</div>
              <div className="rem-temp-step-card">
                <div className="flex-row-between-align-center">
                  <input
                    className="rem-temp-step-title"
                    value={step?.name}
                    placeholder={`${occurrence} ${index + 1}`}
                    onChange={(e) =>
                      onStepChange(step?.id, "name", e?.target?.value)
                    }
                  />
                  {!step?.isAnchor ? (
                    <button
                      type="button"
                      className="rem-temp-remove"
                      onClick={() => onRemoveStep(step?.id)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>

                {step?.isAnchor ? (
                  <div className="rem-temp-note">
                    Completed at time of service — this is the starting point
                    the rest of the chain is calculated from. No reminder needed
                    here; it is part of the visit itself.
                  </div>
                ) : (
                  <>
                    <div className="rem-temp-sentence">
                      <span>Due</span>
                      {renderCompactInput(step?.dueAfterDays, (val) =>
                        onStepChange(step?.id, "dueAfterDays", val),
                      )}
                      <span>
                        days after the previous step’s completed date.
                      </span>
                    </div>
                    <div className="rem-temp-sentence">
                      <span>Remind</span>
                      {renderCompactInput(step?.remindBeforeDays, (val) =>
                        onStepChange(step?.id, "remindBeforeDays", val),
                      )}
                      <span>days before due, then every</span>
                      {renderCompactInput(step?.remindEveryDays, (val) =>
                        onStepChange(step?.id, "remindEveryDays", val),
                      )}
                      <span>days, until completed or</span>
                      {renderCompactInput(step?.expireAfter, (val) =>
                        onStepChange(step?.id, "expireAfter", val),
                      )}
                      <div className="rem-temp-unit">
                        <CustomTextField
                          select
                          fullWidth
                          value={step?.expireUnit}
                          handleChange={(e) =>
                            onStepChange(
                              step?.id,
                              "expireUnit",
                              e?.target?.value,
                            )
                          }
                          chilran={expireUnits.map((unit) => (
                            <MenuItem key={unit.value} value={unit.value}>
                              {unit.name}
                            </MenuItem>
                          ))}
                        />
                      </div>
                      <span>after due — whichever comes first.</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {(values?.steps?.length ?? 0) < 2 ? (
          <div
            className="flex-row-ali-cen rem-temp-add mt10 cursor"
            onClick={onAddStep}
          >
            <div className="flex-center plus-icon">
              <AddIcon sx={addIcon} />
            </div>
            <Typography className="blue-color fs14 ml5 font-bold">
              Add {values?.occurrenceLabel}
            </Typography>
          </div>
        ) : null}

        <div className="flex1-end mt20">
          <div>
            <CustomButton
              text="Save"
              smallBtn
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default ReminderTemplateModal;
